<script>
  // add external libs
  import { scaleThreshold } from 'd3-scale'
  import { csvParse, autoType } from 'd3-dsv'
  import * as topojson from 'topojson-client'
  import { createGeoScales } from '@snlab/florence'
  import DataContainer from '@snlab/florence-datacontainer'

  // svelte components
  import Selection from 'svelte-select'
  import HexMap from './HexMap.svelte'

  // util functions
  import {
    types,
    columnToOddsRatioCI,
    compareColumnsOddsRatio
  } from './utils.js'

  // constants and presets
  let height
  let containerWidth
  let screenWidth
  let screenHeight
  let fullWidth = false
  let width
  let expandedHeight
  let expandedWidth

  const baseColor = '#FFF5EB'
  const countScale = scaleThreshold()
    .domain([0, 100, 1000, 10000, 50000, 1000000])
    .range(['#FFF5EB', '#FDD1A5', '#FD9243', '#DE4F05', '#7F2704'])
  const oddsRatioScale = scaleThreshold()
    .domain([0, 0.5, 0.8, 1 / 0.8, 1 / 0.5, 100000000])
    .range(['#FFF5EB', '#FDD1A5', '#FD9243', '#DE4F05', '#7F2704'])
  const comparisonScale = scaleThreshold()
    .domain([0, 0.5, 0.8, 1 / 0.8, 1 / 0.5, 100000000])
    .range(['#e66101', '#fdb863', '#f7f7f7', '#b2abd2', '#5e3c99'])

  // column selection
  let totalOddsRatio = []
  let totalOddsRatioOpacity = []
  const fig3Selection = 'NAT_ITALY'
  const fig3Label = 'Italian fashion'
  let fig3OddsRatio = []
  let fig3OddsRatioOpacity = []
  let comparison = 'or'
  let comp1Type = 'orComparison'
  let comp2Type = 'orRandom'
  let comp1Selection = {
    value: 'Associated_companies-056',
    label: 'Louis Vuitton'
  }
  let comp2Selection = { value: 'Associated_companies-040', label: 'Gucci' }
  let comp1Data
  let comp2Data
  let comp1Opacity
  let comp2Opacity
  let comp1Scale
  let comp2Scale

  // sandbox related vars
  let sbMode = 'single'

  // data storage
  let topo
  let background
  let table
  let geoScale
  let totals
  let categories
  const categoryLookup = {}
  const top3 = {}
  let hexGeom
  let bgGeom

  // done is set to true once data is loaded
  let done

  // data loading
  async function getData () {
    await Promise.all([
      loadTopo('hex.topojson'),
      loadCSV('hex.csv'),
      loadBackground('background_clean.json'),
      loadMetadata('metadata.json')
    ])
    topo.join(table, { by: ['hex', 'hex'] })
    hexGeom = topo.column('$geometry')
    done = true
  }

  async function loadTopo (url) {
    const response = await fetch(url)
    const data = await response.json()
    topo = new DataContainer(topojson.feature(data, data.objects.hexagons))
  }

  async function loadBackground (url) {
    const response = await fetch(url)
    const data = await response.json()
    background = new DataContainer(data)
    const bbox = background.domain('$geometry')
    geoScale = createGeoScales(bbox)
    bgGeom = background.column('$geometry')
  }

  async function loadCSV (url) {
    const response = await fetch(url)
    const data = await response.text()
    table = new DataContainer(csvParse(data, autoType))
  }

  async function loadMetadata (url) {
    const response = await fetch(url)
    const data = await response.json()
    await loadTotals('col_sums.json')
    categories = data.map(x => {
      const total = Math.ceil(totals[x.shortname] / 25) * 25
      categoryLookup[x.shortname] = { label: x.longname, group: x.category }
      return {
        value: x.shortname,
        label: x.longname + ' (' + total + ' tweets)',
        group: x.category
      }
    }).filter(x => totals[x.value] >= 249)
    categories.sort(function (a, b) {
      return types.indexOf(a.group) - types.indexOf(b.group)
    })
    loadTop3('top3.json')
  }

  async function loadTop3 (url) {
    const response = await fetch(url)
    const data = await response.json()
    for (let index = 0; index < data.length; index++) {
      const element = data[index]
      top3[element.hex] = element.top.map(x => categoryLookup[x]['label'])
    }
  }

  async function loadTotals (url) {
    const response = await fetch(url)
    totals = await response.json()
  }

  getData()

  $: {
    if (done) {
      ({
        or: totalOddsRatio,
        orOpacity: totalOddsRatioOpacity
      } = columnToOddsRatioCI('total', topo.rows(), totals))
    }
  }
  $: {
    if (done) {
      ({
        or: fig3OddsRatio,
        orOpacity: fig3OddsRatioOpacity
      } = columnToOddsRatioCI(fig3Selection, topo.rows(), totals))
    }
  }

  $: {
    if (sbMode === 'sbs') {
      comp1Type = 'count'
      comp2Type = 'orRandom'
      comp1Scale = countScale
      comp2Scale = oddsRatioScale
    }
    if (sbMode === 'sbs2') {
      if (comparison === 'or') {
        comp1Type = 'orRandom'
        comp2Type = 'orRandom'
        comp1Scale = oddsRatioScale
        comp2Scale = oddsRatioScale
      } else {
        comp1Type = 'count'
        comp2Type = 'count'
        comp1Scale = countScale
        comp2Scale = countScale
      }
    }
    if (sbMode === 'single') {
      comp1Type = 'orComparison'
      comp1Scale = comparisonScale
    }
  }

  // calculate comp1
  $: {
    if (done) {
      if (comp1Type === 'orRandom') {
        ({ or: comp1Data, orOpacity: comp1Opacity } = columnToOddsRatioCI(
          comp1Selection.value,
          topo.rows(),
          totals
        ))
        comp1Data = comp1Data.map(oddsRatioScale)
      }
      if (comp1Type === 'count') {
        comp1Data = topo.map(comp1Selection.value, countScale)
        comp1Opacity = 1
      }
      if (comp1Type === 'orComparison') {
        ({ or: comp1Data, orOpacity: comp1Opacity } = compareColumnsOddsRatio(
          comp1Selection.value,
          comp2Selection.value,
          topo.rows(),
          totals
        ))
        comp1Data = comp1Data.map(comparisonScale)
      }
    }
  }

  // calculate comp2
  $: {
    if (done) {
      if (sbMode === 'sbs') {
        comp2Selection = comp1Selection
      }
      if (comp2Type === 'orRandom') {
        ({ or: comp2Data, orOpacity: comp2Opacity } = columnToOddsRatioCI(
          comp2Selection.value,
          topo.rows(),
          totals
        ))
        comp2Data = comp2Data.map(oddsRatioScale)
      } else {
        comp2Data = topo.map(comp2Selection.value, countScale)
        comp2Opacity = 1
      }
    }
  }

  $: {
    if (!fullWidth) {
      expandedWidth = containerWidth
      width = containerWidth
    } else {
      if (sbMode !== 'single') {
        expandedWidth = screenWidth * 0.9 * 0.49
      } else if (screenHeight / screenWidth < 0.55) {
        expandedWidth = (screenHeight / 0.55 - 120) * 0.9
      } else {
        expandedWidth = screenWidth * 0.9
      }
    }
    height = width * 0.55
    expandedHeight = expandedWidth * 0.55
  }

  function switchItalian () {
    comp1Selection = {
      value: 'NAT_ITALY',
      label: 'Italian fashion'
    }
    comp2Selection = { value: 'NAT_FRANCE', label: 'French fashion' }
  }

  function switchGucci () {
    comp1Selection = {
      value: 'Associated_companies-056',
      label: 'Louis Vuitton'
    }
    comp2Selection = { value: 'Associated_companies-040', label: 'Gucci' }
}

  function toggleFullWidth () {
    fullWidth = !fullWidth
  }
</script>

<style>
  .clickChange {
    text-decoration: underline;
    cursor: pointer;
  }

  a {
    @apply underline;
  }

  p {
    @apply font-serif font-light mb-2;
  }

  ul {
    @apply font-serif font-light mb-2 list-disc list-inside;
  }

  h1,
  h2,
  h3,
  text {
    @apply font-sans;
  }

  h1 {
    @apply text-2xl font-semibold;
  }

  h2 {
    @apply text-lg font-semibold mb-1 mt-4;
  }

  h3 {
    @apply italic;
  }

  .overall {
    min-width: 600px;
  }

  .sandboxFullWidth {
    width: 90vw;
    position: relative;
    left: calc(-45vw + 50%);
  }

  .expandButton {
    @apply border bg-green-500 ml-5 py-1 px-2 rounded-full text-white text-xs;
  }

  .shrinkButton {
    @apply border bg-gray-500 ml-5 py-1 px-2 rounded-full text-white text-xs;
  }

  .mapLabel {
    @apply mb-3 text-sm block;
  }

  .modal {
    transition: opacity 0.25s ease;
  }
  body.modal-active {
    overflow-x: hidden;
    overflow-y: visible !important;
  }

  .modalShow {
    @apply opacity-0 pointer-events-none;
  }
  .opacity-95 {
    opacity: 0.95;
  }
</style>

<svelte:window bind:innerHeight={screenHeight} bind:innerWidth={screenWidth} />
<div
  class:modal-active={fullWidth}
  class="overall text-gray-900 font-sans max-w-3xl m-4 p-4 container mx-auto"
  bind:offsetWidth={containerWidth}>
  <h1>The Geography of Fashion Attention</h1>
  <p>
    by
    <a
      href="https://www.atepoorthuis.com"
      rel="noopener noreferrer"
      target="_blank">
      Ate Poorthuis
    </a>
    ,
    <a
      href="https://www.su.se/english/profiles/dpowe-1.282769"
      rel="noopener noreferrer"
      target="_blank">
      Dominic Power
    </a>
    and
    <a
      href="https://geography.as.uky.edu/users/zook"
      rel="noopener noreferrer"
      target="_blank">
      Matthew Zook
    </a>
  </p>
  <p>
    For more detailed information see: Poorthuis, A., Power, D. and M. Zook
    (2019). Attentional Social Media: Mapping the Spaces and Networks of the
    Fashion Industry.
    <span class="italic">
      Annals of the American Association of Geographers
    </span>
    .
    <button
      class="border bg-green-500 py-1 px-2 rounded-full text-white text-xs
      font-sans no-underline">
      <a
        href="https://doi.org/10.1080/24694452.2019.1664887"
        rel="noopener noreferrer"
        target="_blank">
        DOI
      </a>
    </button>
    <button
      class="border bg-green-500 py-1 px-2 rounded-full text-white text-xs
      font-sans no-underline">

      <a
        href="https://github.com/atepoorthuis/geography-of-fashion"
        rel="noopener noreferrer"
        target="_blank">
        CODE & DATA
      </a>
    </button>
  </p>
  <h2>Project Overview</h2>
  <p>
    The key inspiration for this project is that one of fashion’s most
    fundamental inputs is attention.
    <span class="italic">
      Attention to trends. Attention to fads. Attention to brands.
    </span>
    Of course, producing, shipping and selling clothing, accessories and
    cosmetics, is also key, but if a designer or firm doesn’t capture consumers’
    attention, they are toast.
  </p>
  <p>
    Put a slightly more academic way, fashion fundamentally is an economy of
    attention.
  </p>
  <p>
    And since we’re Geographers, we want to know what the geography of fashion
    attention looks like. Attention can be a hard thing to measure and so we use
    a novel indicator – mentions in social media, in this case Twitter – to map
    the spatial manifestation of attention to fashion (see the FAQ below for
    more details). Hence,
    <a
      href="https://www.tandfonline.com/doi/full/10.1080/24694452.2019.1664887"
      rel="noopener noreferrer"
      target="_blank">
      our paper
    </a>
    and the full set of maps here provide a detailed overview of the global, but
    uneven, geography of attention to fashion across designers, companies and
    national origin.
  </p>
  <h2>Exploring These Maps</h2>
  <p>
    There are over 400 different maps (based on different fashion keywords) to
    explore. To make this a little easier, we created an interactive version
    where you can choose and compare different keywords. For instance, you can
    compare where <span class="clickChange" on:click={switchItalian}>Italian fashion garners attention versus where French fashion
    gets noticed</span>. Or see differences between where people discuss the creatives
    behind the business is different to where the individual firms like <span class="clickChange" on:click={switchGucci}>Louis
    Vuitton or Gucci attract attention</span>.
  </p>
  <p>
    Autocomplete is on so try typing a company’s or designer’s name to see your
    choices. Countries are another good choice. It will be most interesting to
    compare counts (overall size) and odds ratio (relative intensivity) for the
    same keyword.
  </p>
  <p>
    EDUCATIONAL ALERT: If you want to understand what these maps are actually
    showing please read the sections below. If not, have fun exploring.
  </p>
  <div
    id="sandbox"
    class="mb-6 mt-6 pb-4 pt-4 border-t border-b border-gray-500">
    <div>
      <div class="flex items-center">
        <label class="mr-3">Display Mode</label>
        <label class="ml-2">
          <input
            class="mr-1"
            type="radio"
            bind:group={sbMode}
            value={'single'} />
          Single Map
        </label>

        <label class="ml-2">
          <input class="mr-1" type="radio" bind:group={sbMode} value={'sbs'} />
          Side by Side
        </label>
        <label class="ml-2">
          <input class="mr-1" type="radio" bind:group={sbMode} value={'sbs2'} />
          Side by Side – Two Terms
        </label>
        {#if screenWidth > 850}
        <button
          class:expandButton={!fullWidth}
          class:shrinkButton={fullWidth}
          on:click={e => toggleFullWidth()}>
          {#if fullWidth}Shrink Maps{:else}Full Screen{/if}
        </button>
        {/if}
      </div>
    </div>
    {#if done}
      <div>
        <label>Choose the keyword(s) to visualize:</label>
        <div class="flex">
          <div class="w-2/5">
            <Selection
              items={categories}
              groupBy={x => x.group}
              bind:selectedValue={comp1Selection}
              isClearable={false} />
          </div>
          {#if sbMode === 'sbs2' || sbMode === 'single'}
            <label class="w-1/5 text-center">vs</label>
            <div class="w-2/5">
              <Selection
                items={categories}
                groupBy={x => x.group}
                bind:selectedValue={comp2Selection}
                isClearable={false} />
            </div>
          {/if}
        </div>
        {#if sbMode === 'sbs2'}
          <div>
            <label>Comparison Type:</label>
            <label>
              <input type="radio" bind:group={comparison} value={'count'} />
              Absolute
            </label>
            <label>
              <input type="radio" bind:group={comparison} value={'or'} />
              Relative
            </label>
          </div>
        {/if}
      </div>
      <div>
        <div class="mx-auto">
          <HexMap
            type={comp1Type}
            {height}
            {width}
            {baseColor}
            fillColors={comp1Data}
            opacity={comp1Opacity}
            colorScale={comp1Scale}
            backgroundGeom={bgGeom}
            geometry={hexGeom}
            comp1Label={categoryLookup[comp1Selection.value].label}
            comp2Label={categoryLookup[comp2Selection.value].label}
            {geoScale} />
        </div>
        <div class="mx-auto">
          {#if sbMode !== 'single'}
            <HexMap
              type={comp2Type}
              {height}
              {width}
              {baseColor}
              fillColors={comp2Data}
              opacity={comp2Opacity}
              colorScale={comp2Scale}
              backgroundGeom={bgGeom}
              geometry={hexGeom}
              {geoScale} />
          {/if}
        </div>
      </div>
    {/if}
  </div>
  <h2>Understanding These Maps - Counts (absolute intensity)</h2>
  <p>
    For example, you could start out with looking at the aggregated total of all
    attention to fashion before narrowing down on attention to fashion from a
    particular designer or country.
  </p>
  {#if done}
    <HexMap
      type="count"
      {height}
      {width}
      {baseColor}
      fillColors={topo.map('total', countScale)}
      colorScale={countScale}
      backgroundGeom={bgGeom}
      geometry={hexGeom}
      colName={'total'}
      hover={true}
      data={topo}
      {top3}
      {geoScale} />
  {/if}
  <label class="mapLabel">Map 1: Total Attention to Fashion (count)</label>
  <p>
    Map 2 represents where tweets about Italian fashion are coming from. Italian
    fashion attracts attention in the entire eastern half and west coast of the
    United States and Canada, western Europe, Turkey and the eastern
    Mediterranean, the urban areas of the Gulf states, and many locations within
    East and Southeast Asia as well as Australia. In short, attention to the
    Italian fashion industry is both widespread and large. Perhaps
    unsurprisingly given the premium price points for much of the fashion
    exported by Italy, attention is concentrated in higher income urban areas.
  </p>
  {#if done}
    <HexMap
      type="count"
      {height}
      {width}
      {baseColor}
      fillColors={topo.map('NAT_ITALY', countScale)}
      colorScale={countScale}
      backgroundGeom={bgGeom}
      geometry={hexGeom}
      {geoScale} />
  {/if}
  <label class="mapLabel">
    Map 2: Total Attention to Italian Fashion (count)
  </label>
  <p>
    Of course, because this is
    <span class="italic">social</span>
    media, tweets are coming from where people are located and so population
    density is driving a lot of the patterns seen in these maps. Note: not every
    place uses the same social media platforms and so lack of attention on
    Twitter does not necessarily mean lack of fashion awareness (See section on
    China below).
  </p>
  <h2>Understanding These Maps - Odds Ratio (relative intensity)</h2>
  <p>
    To augment the population density driven maps of raw counts we also include
    a measure of the relative intensity in places (an Odds Ratio). The odds
    ratio helps control for population and locations with higher scores contain
    much more attention to fashion than would be expected given their overall
    volume of tweets.
  </p>
  {#if done}
    <HexMap
      type="orRandom"
      {height}
      {width}
      {baseColor}
      fillColors={fig3OddsRatio.map(oddsRatioScale)}
      colorScale={oddsRatioScale}
      backgroundGeom={bgGeom}
      opacity={fig3OddsRatioOpacity}
      geometry={hexGeom}
      hover={true}
      data={topo}
      {top3}
      colName={'NAT_ITALY'}
      {geoScale} />
  {/if}
  <label class="mapLabel">
    Map 3: Total Attention to Italian Fashion (odds ratio)
  </label>
  <p>
    Using odds ratios (Map 3) rather than counts (Map 2) for Italian fashion,
    provides a different understanding of these networks of attention. The high
    volumes in much of western Europe drop off when normalized by total amount
    of Twitter activity, although a localized pattern of relatively intense
    attention remains centered around Milan, the center of the Italian fashion
    industry. In the United States, the high volumes likewise do not stand up to
    normalization with much of this region exhibiting less attention relative to
    other things. In contrast, some places – west and southern Africa, northern
    India, etc. – are characterized by relatively more intense interest. Thus,
    these locations represent a particularly specialized knowledge within its
    Twitter user base, namely an interest in Italian fashion.
  </p>
  <h2 class="mt-6 pt-4 border-t border-gray-600">FAQs</h2>
  <h3>What can we learn from this project?</h3>
  <p>
    This project provides evidence for the key geographic patterns within the
    fashion industry that are often talked about but seldom evidentially proven.
    In particular, the data strongly supports the widely held idea of four
    global fashion capitals (London, Milan, New York, Paris). In terms of volume
    of social media output, the diversity of attention paid, and the mix of
    industry specific as well as consumer oriented and consumer side traffic
    these four cities stand out from the rest of the world as especially
    important locales.
  </p>
  <p>
    Moreover, when analyzed with more nuance for industry sector and function we
    see that geographies of attention vary greatly: the attentional geography
    for the business sides of fashion are very different to the marketing and
    consumer oriented sides. Equally geographies of attention vary widely across
    nationality and firm. Such global analysis points to the networked
    dimensions of fashion knowledge/attention and how physically distant places
    and people are differentially connected.
  </p>
  <p>
    It is particularly noteworthy to show how regions – such as sub-Saharan
    Africa, Indonesia or India - that are generally both unrecognized by the
    fashion industry and only very small markets are nevertheless connected to
    the industry in terms of attention. These types of attention may not
    correlate to market size, consumption of products or industry locations but
    to the ability of fashion to insert itself into and be appropriated into
    global as well as highly local aspirational and taste-making discourses and
    world-building. Why not use the tool below to explore in greater detail our
    data? The tool below allows you to use our search terms and data to make
    your own visualizations and see what grabs your attention.
  </p>
  <h3>How Did You Do This?</h3>
  <p>
    Attention can be a hard thing to measure and so we use a novel indicator –
    mentions in social media, in this case Twitter – to map the spatial
    manifestation of attention to fashion. Just as one might measure electrical
    consumption to gauge the size of the aluminum industry or count shipping
    containers to measure trade, mentions of fashion on Twitter provide a useful
    indicator of the attentional dimension of the fashion industry. And metrics
    for fashion attention have hitherto been extremely difficult, if not
    impossible, to do.
  </p>
  <p>
    We drew from a corpus of all geotagged tweets sent from July 2012 to August
    2016 as archived by the DOLLY database in the
    <a
      href="https://geography.as.uky.edu"
      rel="noopener noreferrer"
      target="_blank">
      Department of Geography at the University of Kentucky
    </a>
    . In order to built a set of search terms that is representative of the
    global fashion industry we relied upon data at the
    <a
      href="https://www.businessoffashion.com"
      rel="noopener noreferrer"
      target="_blank">
      Business of Fashion
    </a>
    , a prominent and respected independent fashion website specializing in the
    industrial and business side of the industry. Since 2013 BoF have produced a
    <a
      href="https://www.businessoffashion.com/community/bof500"
      rel="noopener noreferrer"
      target="_blank">
      Global 500
    </a>
    list of individuals that represent the industry. Drawing from three years
    plus the “Hall of Fame” of this list, we generated keyword search terms for
    each individual including their names but also the principal firm and brand
    names they work with or have worked with. An important step (discussed in
    detail in the paper) was filtering out search terms that generated results
    that were primarily unrelated to fashion: e.g. Celine, Chloé, Coach,
    Colette, Elle, or Zara.
  </p>
  <p>
    The end result of this process was a set of 882 fashion keywords
    representing individuals, firms and brands also coded by their country of
    origin and industry sub-sector. For these keywords we pulled all geotagged
    tweets in the DOLLY database to create the analysis and maps in our paper.
    And because we could only include a fraction of these in our journal
    article, we created this website to allow you to explore the full extent of
    our data the geography of fashion attention.
  </p>
  <h3>What’s Up With the Hexagons?</h3>
  <p>
    The large number of data observations makes analysis and visualization
    difficult and this paper uses hexagonal binning – aggregating individual
    tweets to a grid of scale dependent polygons – allowing for efficient data
    aggregation and analysis. The hexagonal bins do not use national or other
    official boundaries reflecting that social media use (and the fashion
    industry itself) does not entirely respect borders, but flows across through
    different cultures, topics and places.
  </p>
  <h3>Why is There Little Attention in China? Or Germany? Or X?</h3>
  <p>
    A related issue of particular importance for global studies is that social
    media practices differ between countries. Perhaps most visible in the case
    of China where the state blocks most Western social media services, this
    also extends to national preferences rooted in culture, custom or history.
    For example, Twitter is quite popular (and sees high use rates) in Brazil
    and Indonesia, especially relative to countries such as Germany. Thus, while
    Twitter is used globally and is the site for active discussions of and
    attention to fashion, other social media platforms could also be used to
    similar effect. These alternative sources, such as Instagram or Facebook,
    come with their own strengths, weaknesses and biases and therefore it is
    essential that we are cautious in our research questions, analysis and
    interpretations of the results.
  </p>
  <h3>Why Use Static Maps Instead of Showing Change Over Time?</h3>
  <p>
    Since our primary purpose has been to address questions about the fashion
    industry’s global geography, we made a choice to create a more static and
    easily-read picture of the industry by combining data over the entire period
    and amalgamating all the search terms and data points over the period. The
    fashion industry does indeed change rapidly and there are many drop-in and
    drop-out names in our search terms. Nonetheless the majority of search terms
    appear year on year and the highest volume search terms appear in all years.
    This is perhaps unsurprising given that fashion is a large global industry
    with high barriers to entry and large investment cycles, meaning that entry
    to the global top takes time and once there people and firms tend to stay at
    the top (at least for a number of years). This is reflected in the number of
    household names and large capital firms and brand names that occur each year
    and dominate the overall twitter traffic studied.
  </p>
  <h3>What are Your Next Steps?</h3>
  <p>
    A particularly innovative part of this approach is the ability to highlight
    networks of attention between places and how these vary by brand and origin.
    As such, we offer not simply a case study but propose a new strategy for
    future research on the geography of the global economy using big data
    sources and methods. Focusing on the geographies of attention help counter
    the long-standing productionist and supply side bias in economic approaches,
    it also allows researchers to understand how these processes differentially
    extends across space, connecting certain cities and sub-locations of cities,
    while bypassing others. Beyond fashion, this approach is also relevant for
    analyzing other sectors of the economy where consumer sentiment and interest
    are crucial factors in product uptake and the global value networks that
    serve consumption.
  </p>
  <h3>What if I want to learn more about this?</h3>
  <p>
    We’d suggest that you start with the paper behind this project and refer to
    the bibliography. It is full of good work. To check out previous work that
    the three of use have done, see below.
  </p>
  <p />
  <ul>
    Fashion and Cultural Industries
    <li>
      Power, D., Zook, M., & Poorthuis, A. (2015).
      <a
        href="http://www.diva-portal.org/smash/get/diva2:911235/FULLTEXT01.pdf"
        rel="noopener noreferrer"
        target="_blank">
        The world tweets Norway: The Norwegian music and fashion industry in
        global social media.
      </a>
    </li>
    <li>
      Power, D., & Hauge, A. (2008).
      <a
        href="https://onlinelibrary.wiley.com/doi/pdf/10.1111/j.1468-2257.2007.00408.x"
        rel="noopener noreferrer"
        target="_blank">
        No man's brand—brands, institutions, and fashion
      </a>
      . Growth and Change, 39(1), 123-143.
    </li>
    <li>
      Hauge, A., Malmberg, A., & Power, D. (2009).
      <a
        href="https://www.tandfonline.com/doi/full/10.1080/09654310802682073"
        rel="noopener noreferrer"
        target="_blank">
        The spaces and places of Swedish fashion.
      </a>
      European Planning Studies, 17(4), 529-547.
    </li>
    <li>
      Power, D., & Scott, A. J. (2004).
      <a
        href="https://books.google.com/books?hl=en&lr=&id=Pnx_AgAAQBAJ&oi=fnd&pg=PP1&dq=Cultural+industries+and+the+production+of+culture&ots=jEiL3pQFtu&sig=7aoYFbHdCVJy1k7Qukv01o3bATg#v=onepage&q=Cultural%20industries%20and%20the%20production%20of%20culture&f=false"
        rel="noopener noreferrer"
        target="_blank">
        Cultural industries and the production of culture.
      </a>
      Routledge.
    </li>
  </ul>
  <ul>
    Using Social Media to Study Urban and Economic Geography
    <li>
      Zook, M. Shelton, T. and A. Poorthuis. (2019).
      <a
        href="https://www.ssrn.com/abstract=2966568"
        rel="noopener noreferrer"
        target="_blank">
        Big Data and the City.
      </a>
      Chapter for Handbook of Urban Geography (Tim Schwanen and Ronald van
      Kempen, Editors). 63-75.
    </li>
    <li>
      Poorthuis, A. and M. Zook. (2017).
      <a
        href="http://dx.doi.org/10.1080/10630732.2017.1335153"
        rel="noopener noreferrer"
        target="_blank">
        Making Big Data Small: Strategies to Expand Urban and Geographical
        Research Using Social Media.
      </a>
      Journal of Urban Technology. Pp 1-21.
    </li>
    <li>
      Poorthuis, A., M. Zook, T. Shelton, M. Graham and M. Stephens. (2017).
      <a
        href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=2513938"
        rel="noopener noreferrer"
        target="_blank">
        Using Geotagged Digital Social Data in Geographic Research.
      </a>
      Book chapter Key Methods in Geography (3rd edition) Sage. Pp. 248-268.
    </li>
    <li>
      Shelton, T., A. Poorthuis. Zook, M. (2015).
      <a
        href="http://dx.doi.org/10.1016/j.landurbplan.2015.02.020"
        rel="noopener noreferrer"
        target="_blank">
        Social media and the city: Rethinking urban socio-spatial inequality
        using user-generated geographic information.
      </a>
      Landscape and Urban Planning. pp. 198–211
    </li>
    <li>
      Zook M, T Shelton, A Poorthuis, R Donohue, M Wilson, M Graham, M Stephens.
      <a
        href="http://manifesto.floatingsheep.org."
        rel="noopener noreferrer"
        target="_blank">
        What would a floating sheep map?
      </a>
      Lexington, KY: Oves Natantes Press, 2015.
    </li>
    <li>
      Shelton, T., Poorthuis, A., Graham, M. and M. Zook. (2014).
      <a
        href="http://dx.doi.org/10.1016/j.geoforum.2014.01.006"
        rel="noopener noreferrer"
        target="_blank">
        Mapping the Data Shadows of Hurricane Sandy: Uncovering the Sociospatial
        Dimensions of ‘Big Data’.
      </a>
      Geoforum. Vol 52 (3), 167-179.
    </li>
    <li>
      Crampton, J. Graham, M., Poorthuis, A., Shelton, T. Stephens, M., Wilson,
      M. & Zook, M. (2013).
      <a
        href="http://dx.doi.org/10.1080/15230406.2013.777137"
        rel="noopener noreferrer"
        target="_blank">
        Beyond the Geotag? Deconstructing “Big Data” and Leveraging the
        Potential of the Geoweb.
      </a>
      Cartography and Geographic Information Science (CaGIS) 40 (2), 130-139.
    </li>
  </ul>

</div>

<div
  class:modalShow={!fullWidth}
  class="modal fixed w-screen h-screen top-0 left-0 flex items-center
  justify-center">
  <div class="modal-overlay absolute w-screen h-screen bg-white opacity-95" />

  <div class="modal-container fixed w-screen h-screen z-50 overflow-y-auto ">

    <!-- Add margin if you want to see grey behind the modal-->
    <div class="modal-content container mx-auto h-auto text-left p-4">

      <!--Title-->
      <div class="flex pb-2">
        <h1>Exploring the Geography of Fashion</h1>
      </div>

      <!--Body-->
      <div id="sandbox" class="mb-4">
        <div>
          <div class="flex items-center">
            <label class="mr-3">Display Mode</label>
            <label class="ml-2">
              <input
                class="mr-1"
                type="radio"
                bind:group={sbMode}
                value={'single'} />
              Single Map
            </label>

            <label class="ml-2">
              <input
                class="mr-1"
                type="radio"
                bind:group={sbMode}
                value={'sbs'} />
              Side by Side
            </label>
            <label class="ml-2">
              <input
                class="mr-1"
                type="radio"
                bind:group={sbMode}
                value={'sbs2'} />
              Side by Side – Two Terms
            </label>
            <button
              class:expandButton={!fullWidth}
              class:shrinkButton={fullWidth}
              on:click={e => toggleFullWidth()}>
              {#if fullWidth}Exit Fullscreen{:else}Fullscreen{/if}
            </button>
          </div>
        </div>
        {#if done}
          <div>
            <label>Choose the keyword(s) to visualize:</label>
            <div class="flex">
              <div class="w-2/5">
                <Selection
                  items={categories}
                  groupBy={x => x.group}
                  bind:selectedValue={comp1Selection}
                  isClearable={false} />
              </div>
              {#if sbMode === 'sbs2' || sbMode === 'single'}
                <label class="w-1/5 text-center">vs</label>
                <div class="w-2/5">
                  <Selection
                    items={categories}
                    groupBy={x => x.group}
                    bind:selectedValue={comp2Selection}
                    isClearable={false} />
                </div>
              {/if}
            </div>
            {#if sbMode === 'sbs2'}
              <div>
                <label>Comparison Type:</label>
                <label>
                  <input type="radio" bind:group={comparison} value={'count'} />
                  Absolute
                </label>
                <label>
                  <input type="radio" bind:group={comparison} value={'or'} />
                  Relative
                </label>
              </div>
            {/if}
          </div>
          <div class:flex={fullWidth}>
            <div class="mx-auto">
              <HexMap
                type={comp1Type}
                height={expandedHeight}
                width={expandedWidth}
                {baseColor}
                fillColors={comp1Data}
                opacity={comp1Opacity}
                colorScale={comp1Scale}
                backgroundGeom={bgGeom}
                geometry={hexGeom}
                comp1Label={categoryLookup[comp1Selection.value].label}
                comp2Label={categoryLookup[comp2Selection.value].label}
                {geoScale} />
            </div>
            {#if sbMode !== 'single'}
            <div class="mx-auto">
                <HexMap
                  type={comp2Type}
                  height={expandedHeight}
                  width={expandedWidth}
                  {baseColor}
                  fillColors={comp2Data}
                  opacity={comp2Opacity}
                  colorScale={comp2Scale}
                  backgroundGeom={bgGeom}
                  geometry={hexGeom}
                  {geoScale} /> 
            </div>
            {/if}
          </div>
        {/if}
      </div>

    </div>
  </div>
</div>
